import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "../api/client";

export interface WorkspaceMembership {
  userId: string;
  email: string;
  displayName: string;
  role: string;
  invitedAt: string | null;
  acceptedAt: string | null;
}

export interface WorkspaceDetail {
  workspace: {
    id: string;
    name: string;
    companyId: string;
    cxoRoleTag: string;
    state: string;
    createdAt: string;
  };
  currentUserRole: string;
  memberships: WorkspaceMembership[];
}

export function useWorkspace(workspaceId: string) {
  return useQuery<WorkspaceDetail, Error>({
    queryKey: ["workspace", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet<WorkspaceDetail>(`/api/workspaces/${workspaceId}`),
    staleTime: 30_000,
  });
}

export interface InviteMemberInput {
  email: string;
  role: "operator" | "approver" | "reviewer" | "agent_handler" | "cxo";
}

export function useInviteMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteMemberInput) =>
      apiPost<{
        invite: {
          id: string;
          token: string;
          email: string;
          role: string;
          expiresAt: string;
          acceptUrl: string;
        };
      }>(`/api/workspaces/${workspaceId}/invites`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
    },
  });
}

export function useRemoveMember(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      apiDelete<unknown>(
        `/api/workspaces/${workspaceId}/memberships/${userId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
    },
  });
}

export function useUpdateMemberRole(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { userId: string; role: InviteMemberInput["role"] }) =>
      apiPatch<unknown>(
        `/api/workspaces/${workspaceId}/memberships/${input.userId}`,
        { role: input.role },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
    },
  });
}

export interface UpdateWorkspaceInput {
  name?: string;
  cxoRoleTag?: string;
  state?: "active" | "paused" | "archived";
}

export function useUpdateWorkspace(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWorkspaceInput) =>
      apiPatch<{ workspace: WorkspaceDetail["workspace"] }>(
        `/api/workspaces/${workspaceId}`,
        input,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspace", workspaceId] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
